import type { MeetingClient } from "./table-call.context.js";
import {
  LEAVE_FALLBACK_TIMEOUT_MS,
  LEAVE_TIMEOUT_MS,
} from "./table-call.provider.constants.js";

export type ClientLeaveOutcome =
  | { status: "completed" }
  | {
      error: unknown;
      fallbackErrors: unknown[];
      status: "rejected" | "timed-out";
    };

interface LocalMeetingMedia {
  audioTrack?: MediaStreamTrack | null;
  rawAudioTrack?: MediaStreamTrack | null;
  rawVideoTrack?: MediaStreamTrack | null;
  screenShareTracks?: {
    audio?: MediaStreamTrack | null;
    video?: MediaStreamTrack | null;
  };
  videoTrack?: MediaStreamTrack | null;
}

const disposalByClient = new WeakMap<
  MeetingClient,
  Promise<ClientLeaveOutcome>
>();
const retiringClients = new WeakSet<MeetingClient>();

const withTimeout = async <Value>(
  promise: Promise<Value>,
  timeoutMs: number,
  message: string
): Promise<Value> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const { promise: timeout, reject } = Promise.withResolvers<never>();
  timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const collectLocalTracks = (client: MeetingClient) => {
  const self = client.self as LocalMeetingMedia | undefined;
  if (!self) {
    return new Set<MediaStreamTrack>();
  }
  return new Set(
    [
      self.audioTrack,
      self.rawAudioTrack,
      self.videoTrack,
      self.rawVideoTrack,
      self.screenShareTracks?.audio,
      self.screenShareTracks?.video,
    ].filter((track): track is MediaStreamTrack => Boolean(track))
  );
};

const stopLocalTracks = (tracks: Set<MediaStreamTrack>) => {
  const errors: unknown[] = [];
  for (const track of tracks) {
    try {
      track.stop();
    } catch (error) {
      errors.push(error);
    }
  }
  return errors;
};

const fallbackCleanup = async (tracks: Set<MediaStreamTrack>) => {
  const errors = stopLocalTracks(tracks);
  // Track stopping is synchronous. Keep the fallback budget explicit for any
  // future SDK-owned cleanup added here, so a failed leave cannot block joins.
  try {
    await withTimeout(
      Promise.resolve(),
      LEAVE_FALLBACK_TIMEOUT_MS,
      "Table call fallback cleanup timed out"
    );
  } catch (error) {
    errors.push(error);
  }
  return errors;
};

export const isClientRetiring = (client: MeetingClient) =>
  retiringClients.has(client);

export const disposeMeetingClient = (client: MeetingClient) => {
  const existing = disposalByClient.get(client);
  if (existing) {
    return existing;
  }

  retiringClients.add(client);
  const tracks = collectLocalTracks(client);
  const leavePromise = client.leave();
  // A leave that settles after its deadline must not become an unhandled
  // rejection while the next session is already joining.
  void (async () => {
    try {
      await leavePromise;
    } catch {
      // The bounded leave path below reports the original result.
    }
  })();
  const disposal = (async (): Promise<ClientLeaveOutcome> => {
    try {
      await withTimeout(
        leavePromise,
        LEAVE_TIMEOUT_MS,
        "Table call leave timed out"
      );
      return { status: "completed" };
    } catch (error) {
      return {
        error,
        fallbackErrors: await fallbackCleanup(tracks),
        status:
          error instanceof Error &&
          error.message === "Table call leave timed out"
            ? "timed-out"
            : "rejected",
      };
    }
  })();
  disposalByClient.set(client, disposal);
  return disposal;
};
